/**
 * Design-QA route for /missions + the Home missions card (mirrors
 * AwardsReview). Fabricated data, no Supabase.
 *
 *   /missions-review              — mid-progress state
 *   /missions-review?state=fresh  — brand-new user, nothing done
 *   /missions-review?state=capped — monthly cap reached
 *   /missions-review?state=loading|failed — async states
 */
import { useSearchParams } from 'react-router';
import { Container, Heading, Stack } from '@fluent/ui';
import { Screen } from '@fluent/ui';
import { MissionsSections } from './missions/MissionsSections.js';
import { MissionsCardView } from './missions/MissionsCard.js';
import type { Mission, MissionsState } from './missions/hooks.js';

const noop = () => undefined;

const M = (
  id: string,
  tier: Mission['tier'],
  coins: number,
  target: number,
  progress: number,
  done = false,
): Mission => ({
  id,
  tier,
  coins,
  target,
  progress,
  completed_at: done ? '2026-07-17T10:00:00Z' : null,
  awarded: done ? coins : null,
});

function build(state: string): MissionsState {
  if (state === 'fresh') {
    return {
      balance: 0,
      month_awarded: 0,
      month_cap: 400,
      missions: CATALOG.map((m) => ({ ...m, progress: 0, completed_at: null })),
    };
  }
  if (state === 'capped') {
    // Real wall physics: earlier completions paid full, the boundary one
    // was clipped, post-wall ones completed but paid nothing (awarded 0
    // renders as +0 — the honest chip states the reviewer must see).
    return {
      balance: 412,
      month_awarded: 400,
      month_cap: 400,
      missions: CATALOG.map((m, i) => ({
        ...m,
        progress: m.target,
        completed_at: '2026-07-17T10:00:00Z',
        awarded: i === 10 ? Math.max(1, m.coins - 1) : i > 10 ? 0 : m.coins,
      })),
    };
  }
  return { balance: 87, month_awarded: 63, month_cap: 400, missions: CATALOG };
}

// Mirrors the live catalog v2 (migration 0016) so the QA route shows the
// real "soo many missions" the tab renders in production.
const CATALOG: Mission[] = [
  M('daily_lessons_1', 'daily', 1, 1, 1, true),
  M('daily_lessons_3', 'daily', 1, 3, 2),
  M('daily_perfect_1', 'daily', 1, 1, 1, true),
  M('daily_exam_task_1', 'daily', 1, 1, 0),
  M('daily_cards_15', 'daily', 1, 15, 9),
  M('daily_cards_30', 'daily', 1, 30, 9),
  M('daily_xp_30', 'daily', 1, 30, 20),
  M('weekly_lessons_5', 'weekly', 2, 5, 5, true),
  M('weekly_lessons_8', 'weekly', 3, 8, 5),
  M('weekly_lessons_15', 'weekly', 5, 15, 5),
  M('weekly_perfect_5', 'weekly', 3, 5, 3),
  M('weekly_perfect_10', 'weekly', 5, 10, 3),
  M('weekly_cards_100', 'weekly', 2, 100, 46),
  M('weekly_cards_250', 'weekly', 4, 250, 46),
  M('weekly_exam_tasks_6', 'weekly', 3, 6, 2),
  M('weekly_fc_sessions_5', 'weekly', 2, 5, 2),
  M('weekly_active_5', 'weekly', 2, 5, 4),
  M('weekly_active_7', 'weekly', 5, 7, 4),
  M('weekly_xp_150', 'weekly', 3, 150, 90),
  M('monthly_lessons_20', 'monthly', 15, 20, 11),
  M('monthly_lessons_40', 'monthly', 25, 40, 11),
  M('monthly_perfect_20', 'monthly', 15, 20, 6),
  M('monthly_cards_500', 'monthly', 15, 500, 240),
  M('monthly_exam_tasks_24', 'monthly', 12, 24, 8),
  M('monthly_fc_sessions_20', 'monthly', 10, 20, 8),
  M('monthly_active_15', 'monthly', 10, 15, 14),
  M('monthly_active_25', 'monthly', 20, 25, 14),
  M('monthly_active_28', 'monthly', 30, 28, 14),
];

export function MissionsReview() {
  const [params] = useSearchParams();
  const state = params.get('state') ?? 'mid';

  if (state === 'loading' || state === 'failed') {
    return (
      <Screen>
        <Container size="sm">
          <Stack gap={4} style={{ padding: '24px 0 64px' }}>
            <MissionsSections
              state={null}
              loading={state === 'loading'}
              failed={state === 'failed'}
              onRetry={noop}
            />
          </Stack>
        </Container>
      </Screen>
    );
  }

  const data = build(state);
  return (
    <Screen>
      <Container size="sm">
        <Stack gap={4} style={{ padding: '24px 0 0' }}>
          <MissionsSections state={data} loading={false} onRetry={noop} />
        </Stack>
      </Container>
      <Container size="sm">
        <Stack gap={3} style={{ padding: '0 0 64px' }}>
          <Heading level={3}>Home card variants</Heading>
          <MissionsCardView state={data} onOpen={noop} />
          <MissionsCardView state={build('capped')} onOpen={noop} />
        </Stack>
      </Container>
    </Screen>
  );
}
